import { T } from '../helpers/helpers';
import React from 'react';
import styled from 'styled-components';
import { panel } from './panelTheme';
import { PropertiesPanelProperty, PropertiesPanelPropertyContent } from './propertiesPanelProperty';
import { PlusIcon, RevealIcon, SwapIcon, TrashIcon } from '../icons/scene';
import { useDropdown } from '../hooks/useDropdown';
import { PropertiesPanelPicker, PropertiesPanelPickerOption } from './propertiesPanelPicker';

// LO SLOT — «in questo posto c'è quell'asset».
//
// È una riga sola, e la stessa riga in tre punti diversi: l'ambiente del World, il materiale di
// un oggetto, la texture di un canale. Il componente non sa di che tipo sia l'asset — l'anteprima
// gliela passa chi lo monta, che sia una sfera, una miniatura o un glifo — ed è il motivo per cui
// è uno solo: cambia il contenuto, non il posto.
//
// I BERSAGLI NON SONO ANNIDATI, ed è la decisione portante. Il difetto classico di questo
// componente è il corpo cliccabile che si mangia i pulsanti dentro di sé, e il rimedio abituale è
// uno `stopPropagation` per pulsante — una riga che se sparisce rompe il componente in silenzio.
// Qui non c'è niente da fermare: sono FRATELLI, e il Tab li percorre tutti.
//
// VUOTO NON È UN CASO A PARTE: è lo stesso posto senza niente dentro. Perde il fondo e prende il
// bordo tratteggiato — il disegno di un posto invece del posto — e dei quattro comandi resta solo
// quello che serve, «mettici qualcosa».
//
// I TRE GESTI SONO TRE COSE DIVERSE, e su quale sta dove non si improvvisa:
//
//     il CORPO — anteprima e nome — porta all'asset. Cliccare il nome di una cosa vuol dire
//         «fammela vedere», non «cambiala»: è la stessa promessa che fa un collegamento;
//     SOSTITUISCI apre l'elenco. È il gesto che cambia, e ha un bottone suo perché cambiare
//         per sbaglio ciò che si voleva solo guardare è il modo più veloce di rovinare una scena;
//     SVUOTA toglie l'assegnazione. Rosso solo mentre lo si tocca.
//
// Prima il corpo apriva la scelta, e il gesto più innocuo — guardare — era quello che modificava.

const PREVIEW_SIZE = '30px';

const Stack = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

// UN AMBIENTE SI GUARDA PRIMA DI REGOLARLO. Una panoramica riconosciuta da una miniatura di
// trenta pixel non è riconosciuta: dove l'asset è un'immagine larga, l'anteprima grande sta sopra
// lo slot e lo slot resta quello di tutti gli altri.
const Hero = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 96px;
    border: 1px solid ${panel.line};
    border-radius: 8px;
    background: ${panel.field};
    color: ${panel.inkMuted};
    overflow: hidden;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }

    svg {
        width: 28px;
        height: 28px;
    }
`;

const Slot = styled.div<{ $empty: boolean }>`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 8px;
    border-radius: 6px;
    border: 1px ${(props) => (props.$empty ? 'dashed' : 'solid')} ${panel.line};
    background: ${(props) => (props.$empty ? 'transparent' : panel.ground)};
    transition:
        background 130ms ease,
        border-color 130ms ease;
`;

// LA FRECCIA STA ADDOSSO ALL'ANTEPRIMA: gli 8px di distanza sono giusti fra due BLOCCHI — il
// corpo e i comandi — non fra una maniglia e la cosa a cui è appesa.
const Reveal = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    align-self: flex-start;
    width: 18px;
    height: 18px;
    margin-right: -10px;
    padding: 0;
    border: 0;
    border-radius: 4px;
    background: transparent;
    color: ${panel.inkMuted};
    cursor: pointer;

    svg {
        width: 12px;
        height: 12px;
    }

    &:hover,
    &:focus-visible {
        color: ${panel.ink};
        outline: none;
    }
`;

const Body = styled.button`
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
    min-width: 0;
    padding: 0;
    border: 0;
    background: transparent;
    text-align: left;
    cursor: pointer;
`;

const Preview = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    width: ${PREVIEW_SIZE};
    height: ${PREVIEW_SIZE};
    border-radius: 6px;
    overflow: hidden;
    color: ${panel.inkMuted};

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }

    svg {
        width: 16px;
        height: 16px;
    }
`;

const Text = styled.span`
    display: flex;
    flex-direction: column;
    gap: 1px;
    min-width: 0;
`;

const Name = styled.span`
    overflow: hidden;
    color: ${panel.ink};
    font-size: 12px;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const Caption = styled.span`
    overflow: hidden;
    color: ${panel.inkMuted};
    font-size: 10.5px;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

// IL ROSSO STA SU UN PULSANTE SOLO, e solo mentre lo si tocca: un cestino già rosso a riposo si
// annuncia distruttivo prima di essere toccato, e in una riga di parametri è un allarme continuo.
//
// INCORNICIATO QUANDO È IL SOLO COMANDO DELLA RIGA. In uno slot pieno i comandi sono due o tre e
// si leggono come un gruppo; in uno slot vuoto ce n'è uno solo, ed è l'unica cosa da fare lì
// dentro: senza cornice galleggerebbe in un rettangolo tratteggiato, e chi guarda deve capire
// dove si clicca senza passarci sopra col puntatore.
const SlotButton = styled.button<{ $danger?: boolean; $framed?: boolean }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    width: ${(props) => (props.$framed ? '26px' : '24px')};
    height: ${(props) => (props.$framed ? '26px' : '24px')};
    padding: 0;
    border: ${(props) => (props.$framed ? `1px solid ${panel.line}` : '0')};
    border-radius: ${(props) => (props.$framed ? '6px' : '4px')};
    background: ${(props) => (props.$framed ? panel.field : 'transparent')};
    color: ${panel.inkMuted};
    cursor: pointer;

    svg {
        width: 15px;
        height: 15px;
    }

    &:hover,
    &:focus-visible {
        outline: none;
        background: ${panel.line};
        color: ${(props) => (props.$danger ? props.theme.app.component.button.destructive.default.backgroundColor : panel.ink)};
    }
`;

// L'ELENCO CHE SI APRE DALLO SLOT È QUELLO DEL DESIGN SYSTEM. Lo slot dice da dove si apre e che
// cosa contiene; come si sceglie — la faccia di un asset, la ricerca, il comando che ne crea uno
// nuovo — è una domanda che si ripete in ogni posto dove si sceglie un asset, e la risposta sta
// scritta una volta in PropertiesPanelPicker.
export type ResourceOption = PropertiesPanelPickerOption;

export type PropertiesPanelResourcePropertyProps = {
    id: string;
    /** L'anteprima grande sopra lo slot. Solo dove guardare l'asset serve davvero: un ambiente. */
    hero?: React.ReactNode;
    /** La miniatura dentro lo slot: una sfera, un'immagine, un glifo. La sceglie chi monta. */
    preview?: React.ReactNode;
    /** Il nome dell'asset. Senza, lo slot è vuoto. */
    name?: React.ReactNode;
    /** La riga sotto il nome: formato, misura, gruppo. */
    caption?: React.ReactNode;
    /** Che cosa si legge quando non c'è niente. */
    emptyLabel?: React.ReactNode;
    /** E che cosa si può fare per riempirlo: la riga sotto, quando lo slot è vuoto. */
    emptyCaption?: React.ReactNode;
    /** Portami dove questo asset vive. */
    onReveal?: () => void;
    /** Le alternative già disponibili. Con queste, lo slot apre da sé il proprio elenco. */
    options?: ResourceOption[];
    /** Quale delle alternative è quella di adesso: nell'elenco si accende. */
    value?: string | number;
    /** Che cosa fare quando si sceglie una voce dell'elenco. */
    onSelect?: (value: string | number) => void;
    /** Il comando in testa all'elenco: «creane uno nuovo». Senza, la riga non c'è. */
    onCreate?: { label: React.ReactNode; onClick: () => void };
    /** Che cosa si cerca, scritto nel campo di ricerca. */
    searchPlaceholder?: string;
    /** La tinta della specie, per le voci dell'elenco senza fotografia. */
    optionTint?: string;
    /** Il corpo: apre la scelta. Serve quando l'elenco non è già qui — un caricamento, una libreria. */
    onPick?: () => void;
    /** Sostituisci — lo stesso posto, un altro asset. */
    onReplace?: () => void;
    /** Svuota. C'è solo se c'è qualcosa da togliere. */
    onClear?: () => void;
    disabled?: boolean;
};

export const PropertiesPanelResourceProperty: React.FC<PropertiesPanelResourcePropertyProps> = ({
    id,
    hero,
    preview,
    name,
    caption,
    emptyLabel,
    emptyCaption,
    options,
    value,
    onSelect,
    onCreate,
    searchPlaceholder,
    optionTint,
    onReveal,
    onPick,
    onReplace,
    onClear,
    disabled
}) => {
    const empty = !name;
    const [openDropdown, closeDropdown, isOpen, Dropdown] = useDropdown();
    const hasList = Boolean(options?.length);

    // Chi ha un elenco lo apre da sé; chi non ce l'ha chiama chi lo monta.
    const choose = (event: React.MouseEvent<HTMLElement>) => {
        if (hasList) openDropdown(event.currentTarget, 'bottom-start');
        else onPick?.();
    };

    const canChoose = hasList || Boolean(onPick);

    // Il corpo porta all'asset; solo dove non c'è dove andare serve a sceglierlo.
    const openBody = (event: React.MouseEvent<HTMLElement>) => (onReveal ? onReveal() : choose(event));

    return (
        <PropertiesPanelProperty disabled={disabled}>
            <PropertiesPanelPropertyContent>
                <Stack>
                    {hero && <Hero aria-hidden='true'>{hero}</Hero>}
                    <Slot id={id} $empty={empty}>
                        {!empty && onReveal && (
                            <Reveal
                                type='button'
                                title={T._('Show where it lives', 'Admin')}
                                aria-label={T._('Show where it lives', 'Admin')}
                                disabled={disabled}
                                onClick={onReveal}
                            >
                                <RevealIcon />
                            </Reveal>
                        )}
                        <Body type='button' disabled={disabled || (!canChoose && !onReveal)} title={onReveal ? undefined : undefined} onClick={openBody}>
                            {preview && <Preview aria-hidden='true'>{preview}</Preview>}
                            <Text>
                                <Name>{empty ? emptyLabel : name}</Name>
                                {empty ? emptyCaption && <Caption>{emptyCaption}</Caption> : caption && <Caption>{caption}</Caption>}
                            </Text>
                        </Body>
                        {empty
                            ? canChoose && (
                                  <SlotButton
                                      type='button'
                                      $framed
                                      title={T._('Choose', 'Admin')}
                                      aria-label={T._('Choose', 'Admin')}
                                      disabled={disabled}
                                      onClick={choose}
                                  >
                                      <PlusIcon />
                                  </SlotButton>
                              )
                            : (onReplace || hasList) && (
                                  <SlotButton
                                      type='button'
                                      title={T._('Replace', 'Admin')}
                                      aria-label={T._('Replace', 'Admin')}
                                      disabled={disabled}
                                      onClick={(event) => (onReplace ? onReplace() : choose(event))}
                                  >
                                      <SwapIcon />
                                  </SlotButton>
                              )}
                        {!empty && onClear && (
                            <SlotButton
                                type='button'
                                $danger
                                title={T._('Clear', 'Admin')}
                                aria-label={T._('Clear', 'Admin')}
                                disabled={disabled}
                                onClick={onClear}
                            >
                                <TrashIcon />
                            </SlotButton>
                        )}
                    </Slot>
                    {isOpen && (
                        <Dropdown>
                            <PropertiesPanelPicker
                                options={options ?? []}
                                current={value}
                                tint={optionTint}
                                searchPlaceholder={searchPlaceholder}
                                onSelect={(chosen) => {
                                    onSelect?.(chosen);
                                    closeDropdown();
                                }}
                                create={
                                    onCreate && {
                                        label: onCreate.label,
                                        // PRIMA SI CHIUDE L'ELENCO: quello che si apre dopo è una domanda —
                                        // il nome — e va fatta su un pannello solo.
                                        onClick: () => {
                                            closeDropdown();
                                            onCreate.onClick();
                                        }
                                    }
                                }
                            />
                        </Dropdown>
                    )}
                </Stack>
            </PropertiesPanelPropertyContent>
        </PropertiesPanelProperty>
    );
};
